import { model as router } from '!/router'
import {
  PrimaryBaseInlineFavoriteToggleButton,
  TVSimpleCard,
  TopTvSimpleCard,
} from '@setplex/arcana'
import { useUnit } from 'effector-react'
import { useCallback, type MouseEvent } from 'react'
import { model as favoritesFeatureModel } from '~/features/favorites'
import * as pinLockFeature from '~/features/pin-lock'
import { model as guestModel } from '~/features/subscriber/guest'
import { updateTvChannel } from '~/widgets/DynamicModule/model/card'
import type { TvWithPrice } from '~/shared/interfaces'
import { TvCardHorizontalCoversMap } from '~/shared/ui/components/cardCovers'
import { paymentTvModelWidget } from '~/widgets/Payment/model'
import { type ViewRow } from '~/widgets/DynamicModule'
import { getCardFonts, getCategoryId } from '../lib'

export const TvChannelCard = ({
  row,
  item,
  idx,
}: {
  row: ViewRow
  item: TvWithPrice
  idx: number
}) => {
  const {
    logoUrl,
    free,
    rented,
    locked,
    formattedName,
    name,
    id,
    labelPrice,
    priceSettings,
    purchaseInfo,
    channelNumber,
    externalId,
  } = item

  const {
    appearance: {
      resource: { search, path },
      style: { posterSize, topContent },
    },
  } = row

  const isLocked = useUnit(pinLockFeature.$isLockedFn)
  const isFeatured = path.includes('featured')
  const isFavorite = search.has('only-favorites')
  const isRecentlyWatched = path.includes('recently-watched')

  const {
    guest,
    favorites,
    updateTvChannelEvent,
    selectActiveChannelEvent,
    routerHref,
  } = useUnit({
    guest: guestModel.$guest,
    favorites: favoritesFeatureModel.$favorites,
    updateTvChannelEvent: updateTvChannel,
    selectActiveChannelEvent: paymentTvModelWidget.selectActiveChannel,
    routerHref: router.$href,
  })

  const handleUpdateChannel = useCallback(
    (e: MouseEvent<Element>) => {
      e.stopPropagation()
      e.preventDefault()
      updateTvChannelEvent({ id: id!, favorite: favorites.channel[id!] })
    },
    [updateTvChannelEvent, id, favorites]
  )

  const categoryId = getCategoryId(search)

  const handleTvCardClick = useCallback(
    (e: MouseEvent<Element>) => {
      e.stopPropagation()
      e.preventDefault()
      selectActiveChannelEvent({
        id: id,
        name: name,
        logoUrl: logoUrl ?? '',
        priceSettings: priceSettings,
        purchaseInfo: purchaseInfo ?? null,
        free: Boolean(free),
        channelNumber: channelNumber ?? 0,
        categoryId: categoryId,
        locked: locked,
        redirect: routerHref ?? '',
        externalId: externalId,
        // TODO: Extra fields. Need to review types for unused fields
        condition: false,
        invalid: false,
        disabled: false,
        errorMessage: '',
        onComplete: () => {},
        onChange: () => {},
        valueAfterInvalid: '',
        featured: isFeatured,
        favorite: isFavorite,
        recentlyWatched: isRecentlyWatched,
      })
    },
    [
      id,
      name,
      logoUrl,
      priceSettings,
      purchaseInfo,
      free,
      channelNumber,
      locked,
      externalId,
      routerHref,
      categoryId,
      selectActiveChannelEvent,
      isFeatured,
      isFavorite,
      isRecentlyWatched,
    ]
  )

  const { titleFont } = getCardFonts(posterSize)

  const Card = topContent ? TopTvSimpleCard : TVSimpleCard
  const topNumber = idx + 1

  return (
    <Card
      onClick={handleTvCardClick}
      posterUrl={logoUrl}
      free={free}
      rented={rented}
      locked={isLocked({ id, locked })}
      purchaseTitle={labelPrice}
      viewedButtonFavorite={!guest}
      title={formattedName}
      titleFont={titleFont}
      channelNumber={channelNumber}
      covers={TvCardHorizontalCoversMap}
      topNumber={topNumber}
    >
      <PrimaryBaseInlineFavoriteToggleButton
        selected={favorites.channel[id!]}
        onClick={handleUpdateChannel}
        dataTestId='favorite-btn'
      />
    </Card>
  )
}
